import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Anchor, Badge, Button, Center, Divider, Group, Loader, Paper, Skeleton, Stack, Text, Timeline, Tooltip } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconAlertCircle, IconFlag, IconSortAscending, IconSortDescending } from '@tabler/icons-react';
import { useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { clearSessionExpiry, logoutSession } from '../api/auth';
import { fetchEscalationStatus, fetchEscalationStatusEnrichment, type EscalationComment, type EscalationStatusPageData } from '../api/result';
import { ApiError } from '../api/http';
import PageShell from '../components/PageShell';
import EscalationTracker from '../components/EscalationTracker';
import InboundCallForm from '../components/InboundCallForm';
import { useProtectedSession } from '../hooks/useProtectedSession';
import { useResolvedProgram } from '../hooks/useResolvedProgram';
import { buildReturnToPath } from '../utils/portalPath';
import { buildPortalApiPath } from '../api/routes';
import { formatters } from '../utils/formatters';
import { isPortalEscalationTerminal } from '../utils/escalationStatus';
import NotFoundPage from './NotFoundPage';

function formatDisplayDate(value?: string | null, dateOnly = false): string | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  if (dateOnly) {
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function applyFormat(value: string, format?: string): string {
  if (!format || !formatters[format]) return value;
  return formatters[format](value);
}

function FieldGrid({ rows, singleColumn = false, rightAlignValues = false, twoColumnLayout = false, detailsLayout = false }: { rows: { label: string; value: string; span?: boolean; isPreview?: boolean; format?: string }[]; singleColumn?: boolean; rightAlignValues?: boolean; twoColumnLayout?: boolean; detailsLayout?: boolean }) {
  const isMobile = useMediaQuery('(max-width: 48em)');
  
  // Details layout: two-column grid with labels above values, except for full-width fields
  if (detailsLayout) {
    const fullWidthRows = rows.filter(row => row.span || row.label === 'Description');
    const gridRows = rows.filter(row => !row.span && row.label !== 'Description');
    
    return (
      <Stack gap="lg">
        {/* Two-column grid: label above value */}
        {gridRows.length > 0 && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: 'var(--mantine-spacing-lg)',
          }}>
            {gridRows.map((row) => (
              <Stack key={row.label} gap={4}>
                <Text size="xs" c="dimmed" fw={500} tt="uppercase">{row.label}</Text>
                <Text size="sm" fw={500} style={{ whiteSpace: 'pre-line' }}>{applyFormat(row.value, row.format)}</Text>
              </Stack>
            ))}
          </div>
        )}
        
        {/* Full-width fields */}
        {fullWidthRows.map((row) => (
          <Stack key={row.label} gap={4}>
            <Text size="xs" c="dimmed" fw={500} tt="uppercase">{row.label}</Text>
            <Text size="sm" fw={500} style={{ whiteSpace: 'pre-line' }}>{applyFormat(row.value, row.format)}</Text>
          </Stack>
        ))}
      </Stack>
    );
  }
  
  const gridTemplateColumns = twoColumnLayout
    ? 'auto 1fr'
    : (isMobile || singleColumn)
      ? 'max-content auto 1fr'
      : 'max-content auto 1fr max-content auto 1fr';
  
  // Split rows into grid rows (default layout) and span rows (full-width)
  const gridRows = rows.filter(row => !row.span);
  const spanRows = rows.filter(row => row.span);
  
  return (
    <Stack gap="md">
      {/* Grid layout for normal fields */}
      {gridRows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns, rowGap: 12, columnGap: 12, alignItems: 'baseline' }}>
          {twoColumnLayout ? (
            // Two-column layout: labels left, values right
            gridRows.flatMap((row) => [
              <Text key={`${row.label}-l`} size="xs" c="dimmed" fw={700} tt="uppercase" ta="left" style={{ whiteSpace: 'nowrap' }}>{row.label}</Text>,
              <Text key={`${row.label}-v`} size="sm" fw={700} ta="right" style={{ whiteSpace: 'nowrap' }}>{applyFormat(row.value, row.format)}</Text>,
            ])
          ) : (
            // Original three-column layout with separator
            gridRows.flatMap((row) => [
              <Text key={`${row.label}-l`} size="xs" c="dimmed" fw={700} tt="uppercase" ta="right" style={{ whiteSpace: 'nowrap' }}>{row.label}</Text>,
              <Text key={`${row.label}-s`} size="xs" c="dimmed">|</Text>,
              <Text key={`${row.label}-v`} size="sm" fw={700} ta={rightAlignValues ? 'right' : 'left'} style={row.label === 'Description' ? { whiteSpace: 'nowrap' } : undefined}>{applyFormat(row.value, row.format)}</Text>,
            ])
          )}
        </div>
      )}
      
      {/* Full-width stacked layout for span fields */}
      {spanRows.map((row) => (
        <Stack key={row.label} gap={4}>
          <Text size="xs" c="dimmed" fw={700} tt="uppercase">{row.label}</Text>
          <Text size="sm" fw={500}>{applyFormat(row.value, row.format)}</Text>
        </Stack>
      ))}
    </Stack>
  );
}

export default function EscalationStatusPage() {
  const { resultId } = useParams<{ resultId: string }>();
  const { data: config, isLoading: configLoading, error: configError, resolvedProgram, sessionScopeKey } = useResolvedProgram();
  const location = useLocation();
  const navigate = useNavigate();
  const sessionQuery = useProtectedSession(sessionScopeKey);
  const logoutMutation = useMutation({ mutationFn: logoutSession });
  const isEscalationIQ = config?.solution === 'escalationiq';
  const inboundCallLoggingEnabled = Boolean(config?.escalations?.capabilities?.inboundCallLoggingEnabled);
  const isMobile = useMediaQuery('(max-width: 48em)');
  const [commentsExpanded, setCommentsExpanded] = useState(false);
  const [commentsSortOldest, setCommentsSortOldest] = useState(false);
  const [submittedComments, setSubmittedComments] = useState<EscalationComment[]>([]);

  const statusQuery = useQuery({
    queryKey: ['escalation-status', resultId, resolvedProgram?.clientName, resolvedProgram?.programName],
    queryFn: () => fetchEscalationStatus('escalations', resultId ?? ''),
    enabled: Boolean(resultId && config && isEscalationIQ && !sessionQuery.isUnauthorized),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: false,
  });

  const statusEnrichmentQuery = useQuery({
    queryKey: ['escalation-status-enrichment', resultId, resolvedProgram?.clientName, resolvedProgram?.programName],
    queryFn: () => fetchEscalationStatusEnrichment('escalations', resultId ?? ''),
    enabled: Boolean(resultId && config && isEscalationIQ && !sessionQuery.isUnauthorized),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: false,
  });

  if (!resultId) {
    return <Navigate to="/escalations/history" replace />;
  }

  if (configLoading || sessionQuery.isLoading) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (sessionQuery.isUnauthorized) {
    return <Navigate to="/auth/request" state={{ returnTo: buildReturnToPath(location) }} replace />;
  }

  if (configError || !config) {
    return <Navigate to="/" replace />;
  }

  if (!isEscalationIQ) {
    return <NotFoundPage />;
  }

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } finally {
      if (sessionScopeKey) clearSessionExpiry(sessionScopeKey);
      navigate('/auth/request', { replace: true });
    }
  };

  const sessionEmail = sessionQuery.data?.email;
  const isRisRxEmployee = sessionEmail?.trim().toLowerCase().endsWith('@risrx.com') === true;
  const statusBase = statusQuery.data;
  
  const status: EscalationStatusPageData | null | undefined = statusEnrichmentQuery.data ?? statusBase;
  const isUrgent = status?.priority?.trim().toUpperCase() === 'URGENT';
  const activityItems = [
    ...(status?.activity ?? []).map((item) => ({
      ...item,
      activityType: 'status' as const,
    })),
    ...[...(status?.comments ?? []), ...submittedComments].map((comment) => ({
      title: 'New Escalation Details Submitted',
      when: comment.timestamp,
      detail: `Comments: ${comment.text}`,
      reason: comment.author ? `Reported by ${comment.author}` : undefined,
      activityType: 'comment' as const,
    })),
  ].sort((a, b) => {
    const dateA = a.when ? new Date(a.when).getTime() : 0;
    const dateB = b.when ? new Date(b.when).getTime() : 0;
    return dateA - dateB;
  });
  // Clients that show the agent name use the "Title | Agent | Date" header on every activity row.
  const activityShowsAgent = config.escalations?.pages.status?.sections.activity?.fields?.includes('agent') === true;
  const inboundCallConfig = inboundCallLoggingEnabled
    ? config.escalations?.pages.status?.sections.inboundCall
    : undefined;
  const statusIsPreview = !statusEnrichmentQuery.data && Boolean(statusBase);
  const statusEnrichmentIsFetching = statusIsPreview && statusEnrichmentQuery.isFetching && !statusEnrichmentQuery.isError;
  const statusEnrichmentHasNoData = Boolean(statusBase) && statusEnrichmentQuery.isSuccess && statusEnrichmentQuery.data === null;
  const showSubmissionProcessingMessage = statusEnrichmentHasNoData || (
    !statusQuery.isLoading &&
    !statusEnrichmentQuery.isLoading &&
    !statusQuery.isError &&
    !statusEnrichmentQuery.isError &&
    !status
  );

  return (
    <PageShell
      config={config}
      containerSize="lg"
      hideTitle
      sessionEmail={sessionEmail}
      onGoHome={() => navigate('/')}
      onLogout={() => { void handleLogout(); }}
      isLoggingOut={logoutMutation.isPending}
    >
      <Stack gap="md">
        <Anchor size="sm" c="dimmed" onClick={() => navigate('/escalations/history')} style={{ cursor: 'pointer' , paddingTop: 20, paddingBottom: 20}}>
          ← All escalations
        </Anchor>

        {/* ── Identity card ── */}
        <Paper p="md" radius="md" withBorder className="portal-panel">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Stack gap="sm" style={{ flex: '0 0 50%', maxWidth: '50%' }}>
              {isRisRxEmployee ? (
                <Text
                  component="button"
                  type="button"
                  className="portal-kicker"
                  tabIndex={-1}
                  onClick={(event) => {
                    if (!event.altKey) return;
                    window.open(
                      buildPortalApiPath(`/escalations/results/${resultId}/beniq`),
                      '_blank',
                      'noopener,noreferrer',
                    );
                  }}
                  style={{ appearance: 'none', background: 'none', border: 0, cursor: 'default', padding: 0, textAlign: 'left' }}
                >
                  Escalation Status
                </Text>
              ) : (
                <Text className="portal-kicker">Escalation Status</Text>
              )}
              {status?.header && status.header.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'max-content max-content 1fr', rowGap: 6, columnGap: 6, alignItems: 'baseline' }}>
                  {status.header.flatMap((row) => [
                    <Text key={`${row.label}-l`} size="sm" c="dimmed" ta="right" style={{ whiteSpace: 'nowrap' }}>{row.label}</Text>,
                    <Text key={`${row.label}-s`} size="sm" c="dimmed">|</Text>,
                    <Text key={`${row.label}-v`} size="sm" fw={700}>{applyFormat(row.value, row.format)}</Text>,
                  ])}
                </div>
              )}
            </Stack>
            <Stack gap={4} align="flex-end">
              {status?.tracker && (
                <Group gap="xs" wrap="nowrap" align="center">
                  {isUrgent && (
                    <IconAlertCircle
                      size={18}
                      color="var(--mantine-color-red-6)"
                      aria-label="Urgent"
                    />
                  )}
                  <Badge
                    size="lg"
                    radius="xl"
                    variant="light"
                    color={status.tracker.isPreview ? 'gray' : status.tracker.color}
                    style={status.tracker.isPreview ? { opacity: 0.65 } : undefined}
                  >
                    {status.tracker.step}
                  </Badge>
                </Group>
              )}
              {status?.submittedAt && (
                <Text size="sm" c="dimmed">
                  Submitted {new Date(status.submittedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                </Text>
              )}
            </Stack>
          </Group>
        </Paper>

        {/* ── Status loading / error states ── */}
        {(statusQuery.isLoading || statusEnrichmentQuery.isLoading) && !status && (
          <Paper p="md" radius="md" withBorder className="portal-panel">
            <Stack gap="sm">
              <Text className="portal-kicker">Status</Text>
              <Text c="dimmed">Fetching status — this may take a moment</Text>
              {[...Array(5)].map((_, i) => (
                <Group key={i} gap="sm" align="flex-start" py={6}>
                  <Skeleton height={24} width={24} circle />
                  <Stack gap={4} style={{ flex: 1 }}>
                    <Skeleton height={14} radius="sm" width="40%" />
                    <Skeleton height={12} radius="sm" width="65%" />
                  </Stack>
                </Group>
              ))}
            </Stack>
          </Paper>
        )}

        {statusQuery.isError && !status && !statusEnrichmentQuery.isError && (() => {
          const httpStatus = statusQuery.error instanceof ApiError ? statusQuery.error.status : undefined;
          const message = httpStatus === 404
            ? 'This escalation could not be found.'
            : 'Unable to load this escalation. It may no longer be available.';
          return (
            <Alert color="red" title="Error">{message}</Alert>
          );
        })()}

        {statusEnrichmentQuery.isError && (() => {
          const httpStatus = statusEnrichmentQuery.error instanceof ApiError ? statusEnrichmentQuery.error.status : undefined;
          const msg = httpStatus === 403
            ? 'Live status tracking is not available for this program.'
            : httpStatus === 404
              ? 'This escalation could not be found.'
              : status
                ? 'The latest status could not be loaded. Showing the most recent information available.'
                : 'Status information is temporarily unavailable. Please try again later.';
          return (
            <Alert color="blue" variant="light">
              <Text size="sm">{msg}</Text>
            </Alert>
          );
        })()}

        {showSubmissionProcessingMessage && (
          <Alert color="blue" variant="light">
            <Text size="sm">
              Your escalation was submitted successfully. Status information is still being processed and may take up to one minute to appear. Please refresh this page to view the latest status.
            </Text>
          </Alert>
        )}

        {/* ── Status data ── */}
        {status && (
          <>
            {/* Progress tracker */}
            <Paper p="xl" radius="md" withBorder className="portal-panel">
              <Stack gap="md">
                <Group
                  justify="space-between"
                  align="center"
                  style={statusEnrichmentIsFetching
                    ? { display: 'grid', gridTemplateColumns: '1fr auto 1fr' }
                    : undefined}
                >
                  <Text className="portal-kicker" size="md">Status</Text>
                  {statusEnrichmentIsFetching && (
                    <Group gap={4} wrap="nowrap" role="status" aria-live="polite" aria-label="Updating status">
                      <Loader size="xs" color="gray" />
                      <Text size="xs" c="dimmed">Updating</Text>
                    </Group>
                  )}
                </Group>
                <EscalationTracker tracker={status.tracker} />
              </Stack>
            </Paper>

            {/* Outcome */}
            {status.outcome && (
              <Paper p="md" radius="md" withBorder className="portal-panel">
                <Stack gap="xs">
                  <Text className="portal-kicker">Outcome</Text>
                  <Group gap="sm">
                    <Text size="sm" c="dimmed">Result:</Text>
                    <Badge color={status.outcome.color}>{status.outcome.result}</Badge>
                  </Group>
                  {status.outcome.reason && (
                    <Group gap="sm">
                      <Text size="sm" c="dimmed">Reason:</Text>
                      <Text size="sm">{status.outcome.reason}</Text>
                    </Group>
                  )}
                  {status.outcome.description && (
                    <Text size="sm">{status.outcome.description}</Text>
                  )}
                  {status.outcome.resolvedAt && (
                    <Group gap="sm">
                      <Text size="sm" c="dimmed">Resolved:</Text>
                      <Text size="sm">{formatDisplayDate(status.outcome.resolvedAt, true)}</Text>
                    </Group>
                  )}
                </Stack>
              </Paper>
            )}

            {/* Activity + Insurance */}
            {(activityItems.length > 0 || inboundCallConfig) && (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile || !activityItems.length || !inboundCallConfig ? '1fr' : '1fr 1fr',
                gap: 'var(--mantine-spacing-md)', 
                alignItems: 'stretch' 
              }}>
                {/* Activity timeline */}
                {activityItems.length > 0 && (
                  <Paper p="md" radius="md" withBorder className="portal-panel">
                    <Stack gap="xs">
                      <Group justify="space-between" align="center" mb="xs">
                        <Text className="portal-kicker">Activity</Text>
                        {statusEnrichmentIsFetching && (
                          <Group gap={4} wrap="nowrap" role="status" aria-live="polite" aria-label="Updating activity">
                            <Loader size="xs" color="gray" />
                            <Text size="xs" c="dimmed">Updating</Text>
                          </Group>
                        )}
                      </Group>
                      {status.transferToRisRx && (
                        // Sits at the top of the Activity panel, above the timeline, so an agent
                        // sees it before reading any activity. The reason BenIQ recorded is free
                        // text of unpredictable length, so it hangs off the callout as a tooltip
                        // rather than taking a line; with no reason the tooltip is disabled.
                        <Tooltip
                          label={status.transferToRisRx.reason}
                          disabled={!status.transferToRisRx.reason}
                          multiline
                          w={260}
                          withArrow
                          events={{ hover: true, focus: true, touch: true }}
                        >
                          <div className="portal-flag-callout">
                            <IconFlag size={16} aria-hidden />
                            <Text size="sm" fw={700}>Transfer to RIS</Text>
                          </div>
                        </Tooltip>
                      )}
                      <Timeline 
                        active={activityItems.length} 
                        bulletSize={12} 
                        lineWidth={1} 
                        style={{ '--tl-color': config.theme.primaryColor } as never}
                        styles={{
                          item: { alignItems: 'center' },
                          itemBody: { paddingTop: 0 },
                          itemTitle: {
                            height: '12px',
                            lineHeight: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            marginTop: 0,
                            marginBottom: 0,
                          }
                        }}
                      >
                        {activityItems.map((item, i) => (
                          <Timeline.Item
                            key={i}
                            bullet={<span />}
                            title={
                              <Group gap="xs" align="center">
                                <Text span size="sm" fw={500}>{item.activityType === 'status' ? (item.outcome ?? item.title) : item.title}</Text>
                                {activityShowsAgent && 'agent' in item && item.agent && (
                                  <>
                                    <Text span size="xs" c="dimmed" aria-hidden>|</Text>
                                    <Text span size="xs" c="dimmed" fw={600}>{item.agent}</Text>
                                  </>
                                )}
                                {item.when && (
                                  <>
                                    {activityShowsAgent && <Text span size="xs" c="dimmed" aria-hidden>|</Text>}
                                    <Text span size="xs" c="dimmed">{formatDisplayDate(item.when, status?.activityWhenDateOnly)}</Text>
                                  </>
                                )}
                              </Group>
                            }
                          >
                            {/* The title row is pinned to 12px high, so the first line below it needs extra room. */}
                            {item.reason && (
                              <Text size="sm" c="dimmed" mt={10}>
                                {item.reason}
                              </Text>
                            )}
                            {item.detail && (
                              <Text size="sm" c="dimmed" mt={item.reason ? 4 : 10} style={{ whiteSpace: 'pre-wrap' }}>
                                {item.detail}
                              </Text>
                            )}
                          </Timeline.Item>
                        ))}
                      </Timeline>
                    </Stack>
                  </Paper>
                )}

                {/* Inbound Call Form */}
                {inboundCallConfig && (
                  <InboundCallForm
                    config={inboundCallConfig}
                    resultId={resultId}
                    primaryColor={config.theme.primaryColor}
                    sessionEmail={sessionEmail}
                    submissionBlocked={isPortalEscalationTerminal(status.tracker.step)}
                    onCommentSubmitted={(comment) => {
                      setSubmittedComments((prev) => [comment, ...prev]);
                    }}
                    onSubmissionSucceeded={async () => {
                      await Promise.all([
                        statusQuery.refetch(),
                        statusEnrichmentQuery.refetch(),
                      ]);
                      // Clear optimistic comments since they're now in the refetched data
                      setSubmittedComments([]);
                    }}
                  />
                )}
              </div>
            )}

            {/* Escalation details */}
            {status.details.length > 0 && (
              <Paper p="md" radius="md" withBorder className="portal-panel">
                <Stack gap="lg">
                  <Text className="portal-kicker">Escalation Submission Details</Text>
                  <FieldGrid rows={status.details} detailsLayout={status.detailsLayoutStyle === 'grid'} />
                  
                  {/* Insurance section */}
                  {status.insurance && status.insurance.length > 0 && (
                    <>
                      <Divider />
                      <div>
                        <FieldGrid
                          rows={status.insurance}
                          detailsLayout
                        />
                      </div>
                    </>
                  )}

                  {/* Comments section */}
                  {(submittedComments.length > 0 || (status.comments && status.comments.length > 0)) && (
                    <>
                      <Divider />
                      <div>
                        <Group justify="space-between" align="center" mb="md">
                          <Text className="portal-kicker">Additional Submission Information</Text>
                          <Button
                            variant="subtle"
                            size="xs"
                            onClick={() => setCommentsSortOldest(!commentsSortOldest)}
                            color={config.theme.primaryColor}
                            leftSection={commentsSortOldest ? <IconSortAscending size={14} /> : <IconSortDescending size={14} />}
                          >
                            {commentsSortOldest ? 'Oldest' : 'Newest'}
                          </Button>
                        </Group>

                        <Stack gap="sm">
                          {[...(status.comments ?? []), ...submittedComments]
                            .sort((a, b) => {
                              const dateA = new Date(a.timestamp).getTime();
                              const dateB = new Date(b.timestamp).getTime();
                              return commentsSortOldest ? dateA - dateB : dateB - dateA;
                            })
                            .slice(0, commentsExpanded ? undefined : 3)
                            .map((comment) => (
                              <Paper
                                key={comment.id}
                                p="sm"
                                withBorder
                                style={{ backgroundColor: '#f8f9fa' }}
                              >
                                <Stack gap={4}>
                                  <Group justify="space-between">
                                    <Text size="sm" fw={600}>
                                      {comment.author}
                                      {comment.authorEmail && (
                                        <Text span fw={400} c="dimmed"> · {comment.authorEmail}</Text>
                                      )}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                      {new Date(comment.timestamp).toLocaleDateString(undefined, {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}
                                    </Text>
                                  </Group>
                                  <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                    {comment.text}
                                  </Text>
                                </Stack>
                              </Paper>
                            ))}

                          {(submittedComments.length + (status.comments?.length ?? 0)) > 3 && (
                            <Button
                              variant="subtle"
                              size="sm"
                              onClick={() => setCommentsExpanded(!commentsExpanded)}
                              color={config.theme.primaryColor}
                            >
                              {commentsExpanded ? 'Hide' : 'Show more'}
                            </Button>
                          )}
                        </Stack>
                      </div>
                    </>
                  )}

                </Stack>
              </Paper>
            )}
          </>
        )}
      </Stack>
    </PageShell>
  );
}


